import {
  CareerSiteOpeningResponse,
  CareerSiteService,
} from 'api-clients/monolith';
import { AppContext } from 'contexts/app-context';
import { useContext, useEffect, useRef, useState } from 'react';
import { LocaleCode } from 'types/locale';

import { TrackingPixel } from 'components/tracking-pixels';

export function useJobs() {
  const {
    accountSlug,
    brandId,
    location,
    setLocation,
    categoryName,
    experienceLevelName,
    activeFilters,
    placeId,
    jobSearchQuery,
    setIsFetchingJobs,
    isMobile,
    isJobsFromCurrentLocation,
    page,
    currentLanguage,
  } = useContext(AppContext);

  const [currentCity, setCurrentCity] = useState<string | null>(null);
  const [isCurrentLocation, setIsCurrentLocation] = useState(
    isJobsFromCurrentLocation,
  );
  const [jobs, setJobs] = useState<CareerSiteOpeningResponse['openings']>([]);
  const [paginationData, setPaginationData] =
    useState<CareerSiteOpeningResponse['pagination']>();
  const [trackingPixels, setTrackingPixels] = useState<
    TrackingPixel[] | undefined
  >([]);

  // Ref to prevent duplicate API calls
  const isFetchingRef = useRef(false);

  // Map frontend language codes to API locale format
  const mapLanguageToLocale = (language: string): string => {
    if (language === 'en') return 'en-US';
    return language || 'en-US';
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!accountSlug) {
        return;
      }

      // Prevent duplicate API calls
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setIsFetchingJobs(true);
      await CareerSiteService.getInternalApiCareerSiteOpenings(
        {
          account_slug: accountSlug,
          brand_id: brandId,
          position_id:
            activeFilters.position.key === 'all'
              ? undefined
              : activeFilters.position.key,
          location_id: location.id ?? undefined,
          experience_level: experienceLevelName ?? undefined,
          place_id: placeId ?? undefined,
          is_jobs_from_current_location: isJobsFromCurrentLocation,
        },
        activeFilters.radius.key,
        activeFilters.sortBy.key,
        activeFilters.category.key,
        activeFilters.compensationType.key,
        page,
        jobSearchQuery ?? undefined,
        activeFilters.department.key === 'all'
          ? undefined
          : activeFilters.department.key,
        activeFilters.location.key,
        undefined, // position parameter (not used since we have position_id in the object)
        mapLanguageToLocale(currentLanguage) as LocaleCode,
      )
        .then((result: CareerSiteOpeningResponse) => {
          if (isMobile && page && page > 1)
            setJobs(prev => [...prev, ...result.openings]);
          else setJobs(result.openings);
          setPaginationData(result.pagination);
          setIsCurrentLocation(result.is_jobs_from_current_location);
          setCurrentCity(result.current_city);
          setTrackingPixels(
            result.tracking_pixels?.map(pixel => ({
              provider: pixel.provider,
              trackingPixel: pixel.tracking_pixel,
              customJavascript: pixel.custom_javascript,
              variables: pixel.variables,
            })) ?? [],
          );
        })
        .catch(() => {
          // TODO: Log error
        })
        .finally(() => {
          isFetchingRef.current = false;
          setIsFetchingJobs(false);
        });
    };
    void fetchData();
  }, [
    accountSlug,
    activeFilters,
    brandId,
    categoryName,
    experienceLevelName,
    page,
    location,
    placeId,
    jobSearchQuery,
    setIsFetchingJobs,
    setLocation,
    isMobile,
    isJobsFromCurrentLocation,
    currentLanguage,
  ]);

  return {
    jobs,
    paginationData,
    isCurrentLocation,
    currentCity,
    trackingPixels,
  };
}
